// The player's queue (HRTH-164.1): playing something from a list (an album, a folder, the
// videos, search results) queues that list from there, like Spotify. Pure, so the rules are
// tested without a media element.

import type { LibraryRef } from '../api/types'

export interface Queue {
  items: LibraryRef[]
  /** The one playing, an index into `items`. */
  at: number
}

/** Past this many seconds into a track, Previous restarts it instead of going back. */
export const RESTART_SECS = 3

const same = (a: LibraryRef, b: LibraryRef) => a.id === b.id && a.library === b.library

/** Plays `item`; with a list it's queued from `item`'s place in it (shuffled: the rest in random order). */
export function startQueue(item: LibraryRef, list?: LibraryRef[], shuffle = false, random = Math.random): Queue {
  const items = list?.length ? [...list] : [item]
  let at = items.findIndex((i) => same(i, item))
  if (at < 0) {
    items.unshift(item)
    at = 0
  }
  if (!shuffle) return { items, at }
  const rest = items.filter((_, n) => n !== at)
  for (let n = rest.length - 1; n > 0; n--) {
    const k = Math.floor(random() * (n + 1))
    ;[rest[n], rest[k]] = [rest[k]!, rest[n]!]
  }
  return { items: [items[at]!, ...rest], at: 0 }
}

export function hasNext(q: Queue): boolean {
  return q.at + 1 < q.items.length
}

export function hasPrevious(q: Queue): boolean {
  return q.at > 0
}

/** The next item, or null at the end of the queue. */
export function next(q: Queue): Queue | null {
  return hasNext(q) ? { ...q, at: q.at + 1 } : null
}

/** What Previous does `secs` into the current item: restart it, or go back one. */
export function previous(q: Queue, secs: number): Queue | 'restart' {
  return secs > RESTART_SECS || !hasPrevious(q) ? 'restart' : { ...q, at: q.at - 1 }
}

/** Puts `item` right after the one playing (moving it if it's queued later already). */
export function playNext(q: Queue, item: LibraryRef): Queue {
  const items = q.items.filter((i, n) => n <= q.at || !same(i, item))
  items.splice(q.at + 1, 0, item)
  return { items, at: q.at }
}

/** Puts `item` at the end of the queue (unless it's already waiting in it). */
export function enqueue(q: Queue, item: LibraryRef): Queue {
  if (q.items.some((i, n) => n > q.at && same(i, item))) return q
  return { items: [...q.items, item], at: q.at }
}

/** What's coming after the one playing. */
export function upNext(q: Queue): LibraryRef[] {
  return q.items.slice(q.at + 1)
}
